import { useEffect, useMemo, useState } from 'react';
import { Platform, Text, View } from 'react-native';
import { useAllDocs } from '../../store/store';
import type { Anexo, Docs } from '../../store/types';
import { ABAS, C, aba } from '../../components/theme';
import { lerAparelho, type Aparelho } from '../../lib/armazenamento';
import { Card } from './comum';
import { t } from '../../lib/i18n';

/** Limite de cada aba no servidor (o mesmo do banco: 2 MB por aba). */
export const LIMITE_ABA = 2 * 1024 * 1024;

export function bytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(1)} GB`;
}
const tamanhoJson = (v: unknown) => { try { return new TextEncoder().encode(JSON.stringify(v)).length; } catch { return JSON.stringify(v ?? '').length; } };

/** Percorre os dados e conta os anexos: na nuvem do usuário, só no aparelho e protegidos pelo cofre. */
function contarAnexos(docs: Docs) {
  const r = { nuvem: 0, aparelho: 0, cofre: 0, links: 0 };
  const visto = (a: Anexo) => {
    if (a.cofre) r.cofre++;
    else if (a.nuvem) r.nuvem++;
    else if (a.url && !a.uri) r.links++;
    else r.aparelho++;
  };
  const andar = (v: any, prof = 0) => {
    if (!v || typeof v !== 'object' || prof > 8) return;
    if (Array.isArray(v)) { v.forEach(x => andar(x, prof + 1)); return; }
    if (Array.isArray(v.anexos)) v.anexos.forEach((a: Anexo) => a && typeof a === 'object' && visto(a));
    if (Array.isArray(v.arquivos)) v.arquivos.forEach((a: Anexo) => a && typeof a === 'object' && 'nome' in a && visto(a));
    for (const k in v) if (k !== 'anexos' && k !== 'arquivos') andar(v[k], prof + 1);
  };
  andar(docs);
  return r;
}

function Barra({ uso, cor }: { uso: number; cor: string }) {
  return (
    <View style={{ height: 8, borderRadius: 4, backgroundColor: C.card2, overflow: 'hidden' }}>
      <View style={{ width: `${Math.min(100, Math.max(2, uso * 100))}%`, height: 8, backgroundColor: uso > 0.85 ? C.danger : uso > 0.65 ? C.warn : cor }} />
    </View>
  );
}

export default function Armazenamento({ cor }: { cor: string }) {
  const docs = useAllDocs();
  const [ap, setAp] = useState<Aparelho | null>(null);
  useEffect(() => { lerAparelho().then(setAp).catch(() => setAp({ livre: null, total: null, anexos: 0 })); }, []);
  const porAba = useMemo(() => Object.entries(docs).map(([id, v]) => ({ id, b: tamanhoJson(v) })).sort((a, b) => b.b - a.b), [docs]);
  const anexos = useMemo(() => contarAnexos(docs), [docs]);
  const total = porAba.reduce((s, x) => s + x.b, 0);
  const maior = porAba[0];
  const nomeDe = (id: string) => {
    if (ABAS.some(a => a.id === id)) return `${aba(id as any).icon} ${aba(id as any).nome}`;
    // 0.72 (texto 10 C7): nomes internos dos dados → nome e ícone que a pessoa reconhece
    if (id === 'diariop') return `${aba('diario').icon} ${aba('diario').nome}`;
    const extra: Record<string, string> = {
      ajustes: '⚙️ ' + t('Ajustes'), lixeira: '🗑️ ' + t('Lixeira'), organizar: '📌 ' + t('Arquivados e fixados'),
      figurinhas: '😀 ' + t('Figurinhas e emojis'), envios: '📨 ' + t('Enviados para o My Planner'),
    };
    return extra[id] ?? '📄 ' + t('Outros dados');
  };

  return (<>
    <Card>
      <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>📱 {Platform.OS === 'web' ? t('Este navegador') : t('Este dispositivo')}</Text>
      {!ap && <Text style={{ color: C.sub }}>{t('Calculando...')}</Text>}
      {ap?.total != null && ap.livre != null && (<>
        <Barra uso={1 - ap.livre / ap.total} cor={cor} />
        <Text style={{ color: C.text }}>{t('{livre} livres de {total}', { livre: bytes(ap.livre), total: bytes(ap.total) })}</Text>
        {ap.livre < 1024 ** 3 && <Text style={{ color: C.warn, fontSize: 12 }}>{t('Pouco espaço livre: fotos e anexos novos podem não ser salvos. Libere espaço ou guarde os anexos no Google Drive ou OneDrive.')}</Text>}
      </>)}
      {ap?.cotaNavegador ? (<>
        <Barra uso={(ap.usoNavegador ?? 0) / ap.cotaNavegador} cor={cor} />
        <Text style={{ color: C.text }}>{t('{uso} usados de {total} reservados para o site', { uso: bytes(ap.usoNavegador ?? 0), total: bytes(ap.cotaNavegador) })}</Text>
      </>) : null}
      {!!ap && Platform.OS !== 'web' && <Text style={{ color: C.sub, fontSize: 13 }}>{t('Anexos guardados aqui no dispositivo: {n}', { n: bytes(ap.anexos) })}</Text>}
    </Card>

    <Card>
      <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>☁️ {t('Seus dados na nuvem do My Planner')}</Text>
      <Text style={{ color: C.sub, fontSize: 13 }}>{t('Total: {n}. Cada aba pode ter até {lim} de texto (fotos e arquivos não contam: ficam no dispositivo ou no seu Drive).', { n: bytes(total), lim: bytes(LIMITE_ABA) })}</Text>
      {porAba.filter(x => x.b > 200).slice(0, 12).map(x => (
        <View key={x.id} style={{ gap: 4 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 8 }}>
            <Text style={{ color: C.text, flex: 1 }} numberOfLines={1}>{nomeDe(x.id)}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{bytes(x.b)} · {Math.round((x.b / LIMITE_ABA) * 100)}%</Text>
          </View>
          <Barra uso={x.b / LIMITE_ABA} cor={cor} />
        </View>
      ))}
      {!!maior && maior.b > LIMITE_ABA * 0.7 && <Text style={{ color: C.warn, fontSize: 12 }}>{t('A aba {aba} está perto do limite. Arquive ou apague itens antigos dela.', { aba: nomeDe(maior.id) })}</Text>}
    </Card>

    <Card>
      <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>📎 {t('Anexos')}</Text>
      <Text style={{ color: C.text }}>☁️ {t('No Google Drive ou OneDrive: {n}', { n: anexos.nuvem })}</Text>
      <Text style={{ color: C.text }}>📱 {t('Só neste dispositivo: {n}', { n: anexos.aparelho })}</Text>
      <Text style={{ color: C.text }}>🔐 {t('Protegidos pelo cofre: {n}', { n: anexos.cofre })}</Text>
      <Text style={{ color: C.text }}>🔗 {t('Links: {n}', { n: anexos.links })}</Text>
      {anexos.aparelho > 0 && <Text style={{ color: C.sub, fontSize: 12 }}>{t('Os anexos que ficam só no dispositivo não aparecem nos outros dispositivos e se perdem se o celular for trocado. Ligue o Google Drive ou o OneDrive em Ajustes para guardar uma cópia.')}</Text>}
    </Card>
  </>);
}
